import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  SubdomainConfig,
  getCurrentSubdomainConfig,
  applyCustomBranding,
  getSubdomain,
  // isSubdomainAccess, // Available but not used in this context
} from "@/utils/subdomain";
import {
  resolveOrgTemplate,
  isAllowedFontCssUrl,
  type OrgTemplate,
} from "@shared/config/orgTemplates";
// updatePWAManifest removed - manifest handled by subdomain-manifest-init.js inline script

interface BrandingContextType {
  isWhitelabel: boolean;
  subdomainConfig: SubdomainConfig | null;
  /** The tenant's whitelabel template, resolved from its institution type.
   *  Defaults to the alumni-institution template until the config loads. */
  template: OrgTemplate;
  loading: boolean;
  error: string | null;
  refreshBranding: () => Promise<void>;
  clearCache: () => void;
}

const BrandingContext = createContext<BrandingContextType>({
  isWhitelabel: false,
  subdomainConfig: null,
  template: resolveOrgTemplate(null),
  loading: true,
  error: null,
  refreshBranding: async () => {},
  clearCache: () => {},
});

/**
 * Load the tenant's custom font stylesheet (allowlisted font CDNs only).
 * Idempotent — replaces any previously injected tenant font link.
 */
const TENANT_FONT_LINK_ID = "tenant-font-css";
function applyTenantFont(config: SubdomainConfig | null) {
  const existing = document.getElementById(TENANT_FONT_LINK_ID);
  const url = config?.fontCssUrl;
  if (!url || !isAllowedFontCssUrl(url)) {
    existing?.remove();
    return;
  }
  if (existing instanceof HTMLLinkElement && existing.href === url) return;
  existing?.remove();
  const link = document.createElement("link");
  link.id = TENANT_FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href = url;
  document.head.appendChild(link);
}

/**
 * Inject the tenant's raw custom CSS (staff-authored, via subdomain_configs.customCss) — the
 * escape hatch for anything the structured theme tokens (colors/font) can't express, like a
 * specific button/border treatment. Staff-only field, same trust level as colors/fonts.
 */
const TENANT_CUSTOM_CSS_ID = "tenant-custom-css";
function applyTenantCustomCss(config: SubdomainConfig | null) {
  const existing = document.getElementById(TENANT_CUSTOM_CSS_ID);
  const css = config?.customCss;
  if (!css || !css.trim()) {
    existing?.remove();
    return;
  }
  if (existing instanceof HTMLStyleElement && existing.textContent === css) return;
  existing?.remove();
  const style = document.createElement("style");
  style.id = TENANT_CUSTOM_CSS_ID;
  style.textContent = css;
  document.head.appendChild(style);
}

interface BrandingProviderProps {
  children: ReactNode;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({
  children,
}) => {
  const [subdomainConfig, setSubdomainConfig] =
    useState<SubdomainConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Only initialize once
    if (initialized) return;

    const loadBrandingConfig = async () => {
      try {
        setLoading(true);
        setError(null);

        // Initializing subdomain branding

        // Get subdomain first to check if we should even try loading config
        const { getSubdomain } = await import("@/utils/subdomain");
        const subdomain = getSubdomain();

        if (!subdomain) {
          setError("No subdomain detected for whitelabel app");
          setSubdomainConfig(null);
          setLoading(false);
          setInitialized(true);
          return;
        }

        // Try to get cached config first to avoid database query
        const { subdomainService } = await import(
          "@/services/subdomain.service"
        );
        let config = subdomainService.getCachedSubdomainConfig(subdomain);

        if (config) {
          setSubdomainConfig(config);
          applyCustomBranding(config);
          applyTenantFont(config);
          applyTenantCustomCss(config);
          // applyCustomBranding already updates title and CSS custom properties
          // Manifest is handled by subdomain-manifest-init.js inline script
          setLoading(false);
          setInitialized(true);

          // Stale-while-revalidate: the cache is valid for 30 min, but a staff change (a
          // freshly uploaded logo, new colors) would otherwise not appear until it expired
          // — which is exactly "web looks great, mobile still shows the old thing." Refresh
          // in the background and apply if anything actually changed.
          const cached = config;
          subdomainService
            .refreshSubdomainConfig(subdomain)
            .then((fresh) => {
              if (fresh && JSON.stringify(fresh) !== JSON.stringify(cached)) {
                setSubdomainConfig(fresh);
                applyCustomBranding(fresh);
                applyTenantFont(fresh);
                applyTenantCustomCss(fresh);
              }
            })
            .catch(() => {});
          return;
        }

        // No cached config, need to fetch from database (only once on app load)
        config = await getCurrentSubdomainConfig();

        if (!config) {
          setError("No branding configuration found for this subdomain");
          setSubdomainConfig(null);
          setLoading(false);
          setInitialized(true);
          return;
        }

        // Applying subdomain branding
        setSubdomainConfig(config);

        // Apply custom branding immediately
        applyCustomBranding(config);
        applyTenantFont(config);
        applyTenantCustomCss(config);

        // Update PWA manifest for subdomain branding using unified service
        try {
          const { updatePWAManifest: updateUnifiedPWAManifest } = await import(
            "../../../../shared/services/pwaManifest.service"
          );
          await updateUnifiedPWAManifest({ subdomainConfig: config as any });
          // Unified PWA manifest updated for subdomain
        } catch (pwaError) {
          // Don't fail the whole branding process for PWA issues
        }

        // Cache the configuration for offline access
        try {
          // Cache will be handled by the service layer
        } catch (cacheError) {
          // Don't fail for caching issues
        }
      } catch (err) {
        setError("Failed to load custom branding");
        setSubdomainConfig(null);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    loadBrandingConfig();
  }, [initialized]);

  // Method to manually refresh branding (for admin updates)
  const refreshBranding = async (): Promise<void> => {
    try {
      // Manual refresh requested
      const { getSubdomain } = await import("@/utils/subdomain");
      const subdomain = getSubdomain();

      if (!subdomain) {
        return;
      }

      const { subdomainService } = await import("@/services/subdomain.service");
      const config = await subdomainService.refreshSubdomainConfig(subdomain);

      if (config) {
        setSubdomainConfig(config);
        applyCustomBranding(config);
        applyTenantFont(config);
        applyTenantCustomCss(config);
        // applyCustomBranding already updates title and CSS custom properties
        setError(null);
        // Branding refreshed successfully
      } else {
        setError("No branding configuration found");
      }
    } catch (err) {
      setError("Failed to refresh branding");
    }
  };

  const clearCache = async () => {
    // Rate limiting to prevent excessive Firebase reads
    const lastClearTime = localStorage.getItem("lastCacheClear");
    const now = Date.now();
    const timeSinceLastClear =
      now - (lastClearTime ? parseInt(lastClearTime) : 0);

    if (timeSinceLastClear < 60000) {
      // 1 minute cooldown
      return;
    }

    localStorage.setItem("lastCacheClear", now.toString());

    const subdomain = getSubdomain();
    if (subdomain) {
      const { subdomainService } = await import("@/services/subdomain.service");
      subdomainService.clearCache(subdomain);

      await refreshBranding();
    }
  };

  // Development helper: Expose cache clearing globally for testing
  // COST WARNING: These functions trigger Firebase reads - use sparingly
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      (window.location.hostname.includes("localhost") ||
        window.location.hostname.includes("127.0.0.1"))
    ) {
      (window as any).clearSubdomainCache = clearCache;
      (window as any).refreshBranding = refreshBranding;
    }
  }, []);

  const value: BrandingContextType = {
    isWhitelabel: subdomainConfig !== null, // Whitelabel app always assumes subdomain context
    subdomainConfig,
    template: resolveOrgTemplate(subdomainConfig),
    loading,
    error,
    refreshBranding,
    clearCache,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
};
